import React from 'react';
import {
  Box,
  TextField,
  FormControl,
  FormLabel,
  FormControlLabel,
  Checkbox,
  Select,
  MenuItem,
  InputLabel,
  FormHelperText,
  Typography,
} from '@mui/material';

// Simplified JSON Schema types (matching backend)
interface BaseSchema {
  title?: string;
  description?: string;
  default?: unknown;
  required?: boolean;
}

interface StringSchema extends BaseSchema {
  type: 'string';
  format?: 'email' | 'uri' | 'date' | 'date-time' | 'tel' | 'uuid';
  enum?: string[];
}

interface NumberSchema extends BaseSchema {
  type: 'number' | 'integer';
  minimum?: number;
  maximum?: number;
}

interface BooleanSchema extends BaseSchema {
  type: 'boolean';
}

interface ObjectSchema extends BaseSchema {
  type: 'object';
  properties: Record<string, JsonSchema>;
  requiredFields?: string[];
}

interface ArraySchema extends BaseSchema {
  type: 'array';
  items: JsonSchema;
}

type JsonSchema =
  | StringSchema
  | NumberSchema
  | BooleanSchema
  | ObjectSchema
  | ArraySchema;

interface SchemaFormRendererProps {
  schema: unknown;
  value: Record<string, unknown>;
  onChange: (newValue: Record<string, unknown>) => void;
  path?: string;
}

/**
 * Recursively renders form fields based on a JSON Schema.
 * Supports: string, number, boolean, enum, object, array types.
 */
export default function SchemaFormRenderer({
  schema: rawSchema,
  value,
  onChange,
  path = '',
}: SchemaFormRendererProps) {
  // Type guard and cast
  const schema = rawSchema as JsonSchema;

  if (!schema || typeof schema !== 'object' || !('type' in schema)) {
    return (
      <Typography variant="body2" color="text.secondary">
        No form schema available
      </Typography>
    );
  }

  // Handle object type (nested form)
  if (schema.type === 'object' && 'properties' in schema) {
    const objectSchema = schema as ObjectSchema;
    const requiredFields = objectSchema.requiredFields || [];

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {objectSchema.title && path === '' && (
          <Typography variant="h6" sx={{ mb: 1 }}>
            {objectSchema.title}
          </Typography>
        )}
        {objectSchema.description && path === '' && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {objectSchema.description}
          </Typography>
        )}
        {Object.entries(objectSchema.properties).map(([key, fieldSchema]) => {
          const fieldPath = path ? `${path}.${key}` : key;
          const isRequired = requiredFields.includes(key);
          const fieldValue = value[key];

          return (
            <FieldRenderer
              key={key}
              name={key}
              schema={fieldSchema}
              value={fieldValue}
              onChange={(newFieldValue) => {
                onChange({ ...value, [key]: newFieldValue });
              }}
              required={isRequired}
              path={fieldPath}
            />
          );
        })}
      </Box>
    );
  }

  // For non-object schemas at root level, wrap in a simple renderer
  return (
    <FieldRenderer
      name="value"
      schema={schema}
      value={value['value']}
      onChange={(newValue) => onChange({ value: newValue })}
      required={schema.required}
      path=""
    />
  );
}

interface FieldRendererProps {
  name: string;
  schema: JsonSchema;
  value: unknown;
  onChange: (value: unknown) => void;
  required?: boolean;
  path: string;
}

function FieldRenderer({
  name,
  schema,
  value,
  onChange,
  required = false,
  path,
}: FieldRendererProps) {
  const label = schema.title || formatLabel(name);
  const helperText = schema.description || '';

  // String with enum -> Select
  if (schema.type === 'string' && 'enum' in schema && schema.enum) {
    const enumSchema = schema as StringSchema & { enum: string[] };
    return (
      <FormControl fullWidth required={required}>
        <InputLabel>{label}</InputLabel>
        <Select
          value={(value as string) || ''}
          onChange={(e) => onChange(e.target.value)}
          label={label}
        >
          {enumSchema.enum.map((option) => (
            <MenuItem key={option} value={option}>
              {option}
            </MenuItem>
          ))}
        </Select>
        {helperText && <FormHelperText>{helperText}</FormHelperText>}
      </FormControl>
    );
  }

  // String (various formats)
  if (schema.type === 'string') {
    const stringSchema = schema as StringSchema;
    let inputType = 'text';
    if (stringSchema.format === 'email') inputType = 'email';
    if (stringSchema.format === 'tel') inputType = 'tel';
    if (stringSchema.format === 'uri') inputType = 'url';
    if (stringSchema.format === 'date') inputType = 'date';
    if (stringSchema.format === 'date-time') inputType = 'datetime-local';

    return (
      <TextField
        fullWidth
        label={label}
        type={inputType}
        value={(value as string) || ''}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        helperText={helperText}
        InputLabelProps={
          inputType === 'date' || inputType === 'datetime-local'
            ? { shrink: true }
            : undefined
        }
      />
    );
  }

  // Number/Integer
  if (schema.type === 'number' || schema.type === 'integer') {
    const numSchema = schema as NumberSchema;
    return (
      <TextField
        fullWidth
        label={label}
        type="number"
        value={value === undefined || value === null ? '' : value}
        onChange={(e) => {
          const val = e.target.value;
          if (val === '') {
            onChange(undefined);
          } else {
            onChange(schema.type === 'integer' ? parseInt(val, 10) : parseFloat(val));
          }
        }}
        required={required}
        helperText={helperText}
        inputProps={{
          min: numSchema.minimum,
          max: numSchema.maximum,
          step: schema.type === 'integer' ? 1 : 'any',
        }}
      />
    );
  }

  // Boolean
  if (schema.type === 'boolean') {
    return (
      <FormControl>
        <FormControlLabel
          control={
            <Checkbox
              checked={Boolean(value)}
              onChange={(e) => onChange(e.target.checked)}
            />
          }
          label={label}
        />
        {helperText && (
          <FormHelperText sx={{ ml: 0 }}>{helperText}</FormHelperText>
        )}
      </FormControl>
    );
  }

  // Array (simple string array for now)
  if (schema.type === 'array') {
    const arrayValue = Array.isArray(value) ? value : [];
    const itemsSchema = (schema as ArraySchema).items;

    // Simple implementation: comma-separated values for string arrays
    if (itemsSchema.type === 'string') {
      return (
        <TextField
          fullWidth
          label={label}
          value={arrayValue.join(', ')}
          onChange={(e) => {
            const items = e.target.value
              .split(',')
              .map((s) => s.trim())
              .filter((s) => s);
            onChange(items);
          }}
          required={required}
          helperText={helperText || 'Enter comma-separated values'}
        />
      );
    }

    // For complex arrays, show a placeholder
    return (
      <FormControl fullWidth>
        <FormLabel>{label}</FormLabel>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Complex array input not yet supported. Please use the additional information field.
        </Typography>
      </FormControl>
    );
  }

  // Object (nested)
  if (schema.type === 'object') {
    return (
      <Box sx={{ pl: 2, borderLeft: 2, borderColor: 'divider' }}>
        <FormLabel>{label}</FormLabel>
        {helperText && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {helperText}
          </Typography>
        )}
        <SchemaFormRenderer
          schema={schema}
          value={(value as Record<string, unknown>) || {}}
          onChange={(newValue) => onChange(newValue)}
          path={path}
        />
      </Box>
    );
  }

  // Unknown type
  return (
    <TextField
      fullWidth
      label={label}
      value={String(value || '')}
      onChange={(e) => onChange(e.target.value)}
      helperText={`Unknown field type: ${schema.type}`}
    />
  );
}

/**
 * Convert camelCase or snake_case to Title Case
 */
function formatLabel(name: string): string {
  return name
    .replace(/([A-Z])/g, ' $1')
    .replace(/_/g, ' ')
    .replace(/^\w/, (c) => c.toUpperCase())
    .trim();
}
